import {
    ApiShopperLandingPage,
    ApiShopperLandingPageLink,
    VLPVariant,
} from '@storefronts/common-types'
import Head from 'next/head'
import Script from 'next/script'
import { VariantWithImage } from '../../types'
import { parseCookie } from '../../utils/helpers'
import { runWhenReady } from './pixelRuntime'

// The Converge queue shim (`window.cvg`) buffers calls until the async pixel
// script at static.runconverge.com/pixels/<token>.js loads and drains them,
// so ready = "shim installed" rather than "vendor script fetched". We signal
// ready from inside the inline snippet via __sfMarkVendorReady because Next's
// `<Script onReady>` fires before the inline body executes.
export const ConvergePixel: React.FC<{
    landingPage: ApiShopperLandingPage
}> = ({ landingPage }) => {
    const token = landingPage.integrations.convergePixelId
    if (!token) return null

    return (
        <>
            <Head>
                <link
                    rel="preconnect dns-prefetch"
                    href="https://static.runconverge.com/"
                    crossOrigin=""
                />
            </Head>
            <Script
                id="converge-pixel"
                strategy="afterInteractive"
                src={`https://static.runconverge.com/pixels/${token}.js`}
                async
            />
            <Script id="converge-init" strategy="afterInteractive">
                {`
                    window.cvg||(function(){var c=window.cvg=function(){c.process?c.process.apply(c,arguments):c.queue.push(arguments)};c.queue=[];})();
                    window.cvg({method:"track",eventName:"$page_load"});
                    window.__sfMarkVendorReady && window.__sfMarkVendorReady('converge');
                `}
            </Script>
        </>
    )
}

export class ConvergeTracker {
    static checkConverge(): boolean {
        return typeof window !== 'undefined' && typeof window.cvg === 'function'
    }

    static trackProductView(
        link: ApiShopperLandingPageLink,
        variant: VLPVariant,
    ) {
        runWhenReady('converge', () => {
            if (!ConvergeTracker.checkConverge()) return

            window.cvg({
                method: 'track',
                eventName: 'Viewed Product',
                properties: {
                    product_id: link.shopifyId,
                    variant_id: variant.shopifyId,
                    name: link.title,
                    price: variant.price.price / 100,
                    currency: 'USD',
                },
            })
        })
    }

    static trackAddToCart(variant: VariantWithImage, _cartId: string) {
        runWhenReady('converge', () => {
            if (!ConvergeTracker.checkConverge()) return

            window.cvg({
                method: 'track',
                eventName: 'Added To Cart',
                properties: {
                    product_id: variant.link.shopifyId,
                    variant_id: variant.shopifyId,
                    name: variant.link.title,
                    price: variant.price.price / 100,
                    currency: 'USD',
                    quantity: 1,
                },
            })
        })
    }

    // Converge stitches sessions across domains via __cvg_uid + __cvg_sid URL
    // params (see runconverge.com/docs cross-domain tracking). Our headless
    // checkout redirect uses window.location.href rather than an <a> click,
    // so the built-in `link_domain` helper can't intercept it — we append the
    // params manually from the storefront's own cookies before navigating.
    // The whole body is wrapped in a try/catch because this runs inline with
    // the checkout redirect: a malformed cookie or URL must never block the
    // customer from reaching checkout.
    static appendCrossDomainParams(url: string): string {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return url
        }
        try {
            const cookies = parseCookie(document.cookie)
            const uid = cookies.__cvg_uid
            const sid = cookies.__cvg_sid
            if (!uid && !sid) return url
            const target = new URL(url, window.location.origin)
            if (uid) target.searchParams.set('__cvg_uid', uid)
            if (sid) target.searchParams.set('__cvg_sid', sid)
            return target.toString()
        } catch {
            return url
        }
    }
}
